import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { nodesApi } from '../services/api';

/**
 * The signed-in user's avatar + dropdown. Extracted from `Layout.js` into `@verlo/shared`
 * so the FOH header and the back-office header share one menu (P7, plan §6).
 *
 * It straddles the surfaces, so the back-office-only pieces — the node liveness fetch and
 * the Node link — are behind a **prop** (`showBackOfficeLinks`) rather than the old
 * build-time `HAS_BACK_OFFICE` flag. On node-ui the prop is `false`, so the `nodesApi` call
 * (a cloud-only router — a 404 on the Pi) never fires and the extra links are not drawn; the
 * component is inert on the kiosk beyond the avatar + Sign Out.
 */

// Liveness values are derived server-side (`heartbeat.liveness`) from the node's last heartbeat.
const NODE_PILL = {
  online:  { label: 'Online',  dot: 'bg-signal-ok-line', cls: 'text-signal-ok bg-signal-ok-tint border-signal-ok-line' },
  stale:   { label: 'Stale',   dot: 'bg-signal-warn-line animate-pulse', cls: 'text-signal-warn bg-signal-warn-tint border-signal-warn-line' },
  pending: { label: 'Pending', dot: 'bg-ink-faint', cls: 'text-ink-soft bg-surface-2 border-hairline' },
  never:   { label: 'No data', dot: 'bg-ink-faint', cls: 'text-ink-soft bg-surface-2 border-hairline' },
  revoked: { label: 'Revoked', dot: 'bg-signal-stop-line',  cls: 'text-signal-stop bg-signal-stop-tint border-signal-stop-line' },
  offline: { label: 'Offline', dot: 'bg-signal-stop-line',  cls: 'text-signal-stop bg-signal-stop-tint border-signal-stop-line' },
  // No node registered for the site yet: not a fault, so not red.
  none:    { label: 'Not set up', dot: 'bg-ink-faint', cls: 'text-ink-soft bg-surface-2 border-hairline' },
};

export const UserDropdown = ({ user, onLogout, showBackOfficeLinks = false }) => {
  const [open, setOpen] = useState(false);
  const [nodeStatus, setNodeStatus] = useState(null);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch latest node status whenever the dropdown opens (cheap cached call).
  // `nodes` is a cloud-only router, so on node-ui this would be a 404 dressed up as
  // "offline" — and the dropdown entry it feeds is not drawn there anyway.
  useEffect(() => {
    if (!open || !showBackOfficeLinks) return;
    let cancelled = false;
    nodesApi.getStatus()
      .then((res) => {
        if (cancelled) return;
        setNodeStatus(res.data?.registered === false ? 'none' : (res.data?.node?.liveness || 'offline'));
      })
      .catch(() => { if (!cancelled) setNodeStatus('offline'); });
    return () => { cancelled = true; };
  }, [open, showBackOfficeLinks]);

  const nodePill = NODE_PILL[nodeStatus] || null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-paper transition-colors"
        data-testid="user-menu-btn"
      >
        <div className="w-8 h-8 rounded-full bg-verlo flex items-center justify-center">
          <span className="text-white text-xs font-bold">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
        </div>
        <span className="text-sm font-medium text-ink-soft hidden xl:block">{user?.name}</span>
        <ChevronDown size={14} className={`text-ink-faint transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-60 bg-white border border-hairline rounded-lg shadow-lg z-50 py-1" data-testid="user-dropdown-menu">
          <div className="px-3 py-2 border-b border-hairline">
            <p className="text-sm font-medium text-ink">{user?.name}</p>
            <p className="text-xs text-ink-soft">{user?.email || user?.role}</p>
          </div>
          {/* The node fleet page is a cloud-ui route. */}
          {showBackOfficeLinks && (
            <>
              {/* Your own sign-in: two-factor, the devices you're signed in on, where you land. */}
              <button
                onClick={() => { setOpen(false); navigate('/back-office/user-settings'); }}
                className="w-full text-left px-3 py-2 text-sm text-ink-soft hover:bg-surface-2 transition-colors"
                data-testid="dropdown-my-settings-btn"
              >
                My settings
              </button>
              <button
                onClick={() => { setOpen(false); navigate('/node'); }}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-ink-soft hover:bg-surface-2 transition-colors"
                data-testid="dropdown-node-btn"
              >
                <span>Verlo Node</span>
                {nodePill ? (
                  <span
                    className={`inline-flex items-center gap-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold ${nodePill.cls}`}
                    data-testid={`dropdown-node-status-${nodeStatus}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${nodePill.dot}`} />
                    {nodePill.label}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold text-ink-faint bg-surface-2 border-hairline" data-testid="dropdown-node-status-loading">
                    <span className="w-1.5 h-1.5 rounded-full bg-ink-faint animate-pulse" />
                    …
                  </span>
                )}
              </button>
              <div className="border-t border-hairline my-1" />
            </>
          )}
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full text-left px-3 py-2 text-sm text-ink-soft hover:bg-surface-2 transition-colors"
            data-testid="dropdown-logout-btn"
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
